import { Box, BoxProps, Theme, Typography, useTheme } from "@mui/material";
import { CarPortfolioChartItem } from "../asset-allocation-types";
import { CarSelectChartField } from "./SelectChartField";
import { ComponentProps, useCallback, useMemo } from "react";
import { Chart } from "react-chartjs-2";
import { formatPercentFactor } from "@app/utils";
import {
  ChartTooltip,
  RenderTooltip,
  useChartTooltip,
} from "@app/components/ChartTooltip";
import { UseChartDataSelector } from "./useChartDataSelector";
import { createPendoClassName } from "@app/core/thirdParty/pendo";
import { ChartPlugins, ChartUtils } from "@app/utils/chartUtils";
import { ChartDataset } from "chart.js";

type Options = ComponentProps<typeof Chart>["options"];

interface MultiPeriodReturnComparisonChartData {
  datasets: ChartDataset<"bar", number[]>[];
  yLabel: string;
  xLabels: string[];
}

export const getMultiPeriodReturnComparisonChartData = ({
  theme,
  data,
  data1Id,
  data2Id,
  forPdf,
}: {
  theme: Theme;
  data: CarPortfolioChartItem[];
  data1Id: string;
  data2Id: string;
  forPdf?: boolean;
}): MultiPeriodReturnComparisonChartData => {
  const item1 = data.find((i) => i.id === data1Id);
  const item2 = data.find((i) => i.id === data2Id);

  if (!item1 || !item2) {
    return {
      datasets: [],
      yLabel: "",
      xLabels: [],
    };
  }

  return {
    datasets: [
      {
        id: "bar1",
        type: "sharpBar" as "bar",
        label: item1.label, //`${item1?.label ?? ""}\n(No fee)`,
        data: item1.y ?? [],
        backgroundColor: theme.palette.chartsColor.green,
        barPercentage: forPdf ? 0.9 : 0.95,
        categoryPercentage: forPdf ? 0.6 : 0.65,
      },
      {
        id: "bar2",
        type: "sharpBar" as "bar",
        label: item2.label, //`${item2?.label ?? ""}\n(No fee)`,
        data: [1, 2, 3, 4], //item2.y ?? [],
        backgroundColor: theme.palette.chartsColor.orange,
        barPercentage: forPdf ? 0.9 : 0.95,
        categoryPercentage: forPdf ? 0.6 : 0.65,
      },
    ],
    // bar1MaxFee: item1?.yMaxFee
    //   ? {
    //       data: item1?.yMaxFee ?? [],
    //       label: `${item1?.label ?? ""}\n(Net of max. fee TODO)`,
    //     }
    //   : undefined,
    // bar2MaxFee: item2?.yMaxFee
    //   ? {
    //       data: item2?.yMaxFee ?? [],
    //       label: `${item2?.label ?? ""}\n(Net of max. fee TODO)`,
    //     }
    //   : undefined,
    yLabel: "Cumulative Return (%)",
    xLabels: ["3 Month", "1 Year", "3 Year", "5 Year", "10 Year"],
  };
};

interface InternalChartProps {
  sx?: BoxProps["sx"];
  data: CarPortfolioChartItem[];
  data1Id: string;
  data2Id: string;
  pendoPrefix: string;
  isWhiteContext?: boolean;
}

const InternalChart = ({
  sx,
  data,
  data1Id,
  data2Id,
  pendoPrefix,
  isWhiteContext,
}: InternalChartProps) => {
  const theme = useTheme();
  const { tooltipPlugin, tooltipData } = useChartTooltip();
  const { datasets, yLabel, xLabels } = useMemo(
    () =>
      getMultiPeriodReturnComparisonChartData({
        theme,
        data,
        data1Id,
        data2Id,
      }),
    [theme, data, data1Id, data2Id],
  );

  const renderTooltip: RenderTooltip = useCallback(
    (datasetIndex, dataIndex) => {
      const ds = datasets.at(datasetIndex);
      const value = ds?.data?.[dataIndex] ?? 0;
      const periodStr = xLabels[dataIndex] ?? "";
      return (
        <Box
          sx={{
            fontSize: 15,
            fontWeight: 600,
            color: "black",
            whiteSpace: "nowrap",
          }}
        >
          <Typography variant="par01SemiBold" sx={{ fontSize: 15 }}>
            {ds?.label}
          </Typography>
          <br />
          <Typography variant="par01Regular" sx={{ fontSize: 13 }}>
            {periodStr}
          </Typography>
          <br />
          <Typography variant="par01Regular" sx={{ fontSize: 13 }}>
            {formatPercentFactor(value, 1)}
          </Typography>
        </Box>
      );
    },
    [datasets, xLabels],
  );

  const options: Options = useMemo(
    () => ({
      maintainAspectRatio: false,
      animation: {
        duration: 500,
      },
      layout: {
        padding: {
          top: 0,
          left: 0,
          bottom: 0,
          right: 1, // for some reason right border is cropped, so add right padding to fit it in image
        },
      },
      scales: {
        x: {
          ticks: {
            font: {
              family: theme.typography.fontFamily,
              size: 13,
              weight: 600,
            },
            color: theme.palette.softBlack,
          },
          grid: {
            display: false,
          },
          type: "category",
          labels: xLabels,
        },
        y: {
          // offset: true,
          suggestedMax: 4.0,
          // suggestedMin: -0.1,
          title: {
            text: yLabel,
            display: true,
            font: {
              family: theme.typography.fontFamily,
              size: 15,
              weight: 600,
            },
          },
          ticks: {
            font: {
              family: theme.typography.fontFamily,
              size: 15,
              weight: 600,
            },
            color: theme.palette.softBlack,
            callback: (value) =>
              typeof value === "number" ? formatPercentFactor(value) : value,
          },
          grid: ChartUtils.getGridOptions({
            theme,
            showMinMaxLines: true,
            highlightZero: true,
          }),
        },
      },
      plugins: {
        tooltip: tooltipPlugin,
        roundedBackground: ChartUtils.getRoundedBackgroundOptions({
          theme,
          isWhiteContext,
        }),
      },
    }),
    [theme, tooltipPlugin, isWhiteContext, yLabel, xLabels],
  );

  return (
    <Box
      sx={{
        width: "100%",
        height: 460,
        position: "relative",
        display: "flex",
        ...sx,
      }}
    >
      <ChartTooltip tooltipData={tooltipData} renderTooltip={renderTooltip} />
      <Chart
        className={createPendoClassName(`${pendoPrefix}_chart`)}
        type="bar"
        datasetIdKey="id"
        data={{
          datasets,
        }}
        options={options}
        plugins={[ChartPlugins.roundedBackground]}
      />
    </Box>
  );
};

interface ChartMultiPeriodReturnComparisonProps {
  selectorLabel1: string;
  selectorLabel2: string;
  data: CarPortfolioChartItem[];
  chartDataSelector: UseChartDataSelector;
  pendoPrefix: string;
  isWhiteContext?: boolean;
}

export const ChartMultiPeriodReturnComparison = ({
  selectorLabel1,
  selectorLabel2,
  data,
  chartDataSelector: { data1Id, data2Id, onData1IdChange, onData2IdChange },
  pendoPrefix,
  isWhiteContext,
}: ChartMultiPeriodReturnComparisonProps) => {
  const theme = useTheme();
  return (
    <>
      <InternalChart
        data={data}
        data1Id={data1Id ?? ""}
        data2Id={data2Id ?? ""}
        pendoPrefix={pendoPrefix}
        isWhiteContext={isWhiteContext}
      />
      <Box
        sx={{
          mt: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          columnGap: 3,
        }}
      >
        <CarSelectChartField
          sx={{
            alignSelf: "flex-start",
            width: 200,
            ".MuiInputBase-root": {
              backgroundColor: isWhiteContext ? "gray1" : "white",
            },
          }}
          label={selectorLabel1}
          value={data1Id}
          onChange={onData1IdChange}
          items={data}
          chartColor={theme.palette.chartsColor.green}
        />
        <CarSelectChartField
          sx={{
            alignSelf: "flex-start",
            width: 200,
            ".MuiInputBase-root": {
              backgroundColor: isWhiteContext ? "gray1" : "white",
            },
          }}
          label={selectorLabel2}
          value={data2Id}
          onChange={onData2IdChange}
          items={data}
          chartColor={theme.palette.chartsColor.orange}
        />
      </Box>
    </>
  );
};
